/**
 * Conversations API client
 */
import apiClient from './client';

export interface Attachment {
  id: string;
  message_id: string;
  filename: string;
  mime_type: string;
  file_size: number;
  width?: number;
  height?: number;
  data_base64: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  provider?: string;
  tokens_used?: number;
  feature_mode?: string;
  validation_passed?: boolean;
  has_doctrinal_warning?: boolean;
  created_at: string;
  attachments?: Attachment[];
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string | null;
  response_verbosity: 'concise' | 'detailed';
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  messages?: Message[];
}

export interface CreateConversationRequest {
  title?: string;
}

export interface UpdateConversationRequest {
  title: string;
}

export interface UpdateVerbosityRequest {
  verbosity: 'concise' | 'detailed';
}

export interface AddMessageRequest {
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  provider?: string;
  tokens_used?: number;
}

export const conversationsApi = {
  /**
   * Create a new conversation
   */
  async createConversation(data?: CreateConversationRequest): Promise<Conversation> {
    const response = await apiClient.post('/api/conversations', data || {});
    return response.data;
  },

  /**
   * Get a conversation with its messages
   */
  async getConversation(conversationId: string): Promise<Conversation> {
    const response = await apiClient.get(`/api/conversations/${conversationId}`);
    return response.data;
  },

  /**
   * List user's conversations
   */
  async listConversations(limit: number = 50, offset: number = 0): Promise<Conversation[]> {
    const response = await apiClient.get('/api/conversations', {
      params: { limit, offset }
    });
    return response.data;
  },

  /**
   * Update conversation title
   */
  async updateConversation(
    conversationId: string,
    data: UpdateConversationRequest
  ): Promise<Conversation> {
    const response = await apiClient.patch(`/api/conversations/${conversationId}`, data);
    return response.data;
  },

  /**
   * Update conversation verbosity setting
   */
  async updateVerbosity(
    conversationId: string,
    data: UpdateVerbosityRequest
  ): Promise<Conversation> {
    const response = await apiClient.patch(`/api/conversations/${conversationId}/verbosity`, data);
    return response.data;
  },

  /**
   * Delete (soft delete) a conversation
   */
  async deleteConversation(conversationId: string): Promise<void> {
    await apiClient.delete(`/api/conversations/${conversationId}`);
  },

  /**
   * Add a message to a conversation
   */
  async addMessage(
    conversationId: string,
    data: AddMessageRequest
  ): Promise<Message> {
    const response = await apiClient.post(
      `/api/conversations/${conversationId}/messages`,
      data
    );
    return response.data;
  },

  /**
   * Get messages for a conversation
   */
  async getMessages(conversationId: string): Promise<Message[]> {
    const response = await apiClient.get(`/api/conversations/${conversationId}/messages`);
    return response.data;
  },

  /**
   * Sync conversation to clipboard
   */
  async syncToClipboard(conversationId: string): Promise<any> {
    const response = await apiClient.post(
      `/api/clipboard/items/from-conversation/${conversationId}`
    );
    return response.data;
  }
};
